import React from 'react'
import { useQuery } from 'react-query'
import { Responses } from '../../../../global'
// @ts-ignore
import { getAddress } from '../../../api'
import { ContactInfoCopyFieldsProps } from '../../../../widgets/ContactInfoCopyFields'

const RECAPTCHA_STALE_TIME = 120000 // 2 minutes

function groomData(data?: Responses.Address | null) {
  if (data) {
    return {
      address: {
        country: data.country,
        postalCode: data.zip,
        city: data.city,
        state: data.state,
        streetAddress1: data.streetAddress1,
        streetAddress2: data.streetAddress2,
      },
      fullName: data.name,
    } as ContactInfoCopyFieldsProps
  }

  return null
}

export interface UseContactInfoAddressProps {
  /**
   * Needed to locate the address.
   */
  addressId: string
  /**
   * If true, it won't attempt to fetch the address.
   * @default false
   */
  isShippingAddressPrivate?: boolean
  /**
   * A ref to the ReCAPTCHA element.
   */
  recaptchaRef: React.MutableRefObject<any | null>
}

/**
 * Hook to control the retrieval of the registry owner's address information, with some performance optimizations
 * as well as data reshaping.
 */
export default function useContactInfoAddress({
  addressId,
  isShippingAddressPrivate = false,
  recaptchaRef,
}: UseContactInfoAddressProps) {
  const query = useQuery(
    addressId,
    // @ts-ignore
    async (): Promise<Responses.Address | null> => {
      // Attempt to fetch address (GG may already be whitelisted)
      let address
      try {
        address = await getAddress(addressId)
      } catch (error) {
        // If not whitelisted, authenticate with recaptcha token
        const token = await recaptchaRef?.current.executeAsync()
        try {
          address = await getAddress(addressId, token)
        } catch (error) {
          // Address may be nil. When it is we respond 404. Our implementation of
          // fetch() throws all non-200-level statuses. Catching here to prevent
          // error from getting reported in TrackJS.
          address = null
        }
      }
      return address
    },
    {
      // If we know that the shipping address is private, there is no need to perform a request
      enabled: !isShippingAddressPrivate,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: RECAPTCHA_STALE_TIME,
    }
  )

  return { ...query, data: groomData(query.data) }
}
